import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Inbox, X } from 'lucide-react';

import { decidirPedido, type PedidoRemocao, type StatusPedido, type TipoCompartilhavel } from '@/api/compartilhamento';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { formatDate } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';

const NOME_DO_TIPO: Record<TipoCompartilhavel, string> = {
  PROCESSO: 'Processo',
  CLIENTE: 'Cliente',
  ARTEFATO: 'Documento',
  MODELO_PETICAO: 'Modelo',
};

const STATUS: Record<StatusPedido, { rotulo: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  PENDENTE: { rotulo: 'Aguardando', variant: 'default' },
  APROVADO: { rotulo: 'Retirado', variant: 'secondary' },
  RECUSADO: { rotulo: 'Recusado', variant: 'destructive' },
  CANCELADO: { rotulo: 'Cancelado', variant: 'outline' },
};

interface PedidosAbaProps {
  escritorioId: string;
  pedidos: PedidoRemocao[];
  /** É o Proprietário: decide os pedidos dos outros. */
  podeDecidir: boolean;
}

/**
 * Os pedidos de retirada do escritório (docs/PLANO_COMPARTILHAMENTO_ESCRITORIO.md A10, Bloco 4c).
 * O Proprietário aprova ou recusa os de todos; cada membro acompanha e cancela os próprios.
 */
export function PedidosAba({ escritorioId, pedidos, podeDecidir }: PedidosAbaProps) {
  if (pedidos.length === 0) {
    return (
      <EmptyState
        icon={<Inbox />}
        title="Nenhum pedido"
        description={
          podeDecidir
            ? 'Quando um membro pedir para tirar algo do escritório, o pedido aparece aqui para você decidir.'
            : 'Para tirar de vez algo que você compartilhou, use "Compartilhar no escritório…" no item. O Proprietário decide aqui.'
        }
      />
    );
  }
  return (
    <div className="space-y-2">
      {pedidos.map((p) => (
        <PedidoCard key={p.id} escritorioId={escritorioId} pedido={p} podeDecidir={podeDecidir} />
      ))}
    </div>
  );
}

function PedidoCard({
  escritorioId,
  pedido,
  podeDecidir,
}: {
  escritorioId: string;
  pedido: PedidoRemocao;
  podeDecidir: boolean;
}) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const decidir = useMutation({
    mutationFn: (acao: 'aprovar' | 'recusar' | 'cancelar') => decidirPedido(escritorioId, pedido.id, acao),
    onSuccess: (r) => {
      showSuccess(
        r.status === 'APROVADO'
          ? pedido.acao === 'EXCLUIR'
            ? 'Processo excluído'
            : 'Retirado do escritório'
          : r.status === 'RECUSADO'
            ? 'Pedido recusado'
            : 'Pedido cancelado',
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.pedidos(escritorioId) });
      queryClient.invalidateQueries({ queryKey: ['escritorios', 'item'] });
      if (r.status === 'APROVADO') {
        for (const lista of [
          queryKeys.processos.all,
          queryKeys.comunicacoes.all,
          queryKeys.clientes.all,
          queryKeys.documentos.all,
          queryKeys.modelos.all,
        ]) {
          queryClient.invalidateQueries({ queryKey: lista });
        }
      }
    },
    onError: (err) => notifyMutationError(err, showError),
  });
  const pendente = pedido.status === 'PENDENTE';
  const status = STATUS[pedido.status];
  const acao = decidir.isPending ? decidir.variables : null;
  const decide = podeDecidir && !pedido.meu;
  const exclusao = pedido.acao === 'EXCLUIR';

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {exclusao ? 'Excluir o processo' : NOME_DO_TIPO[pedido.recurso_tipo]}
            </span>
            <Badge variant={status.variant}>
              {exclusao && pedido.status === 'APROVADO' ? 'Excluído' : status.rotulo}
            </Badge>
          </div>
          <p className="break-words font-medium">{pedido.rotulo ?? 'Item excluído'}</p>
          <p className="text-xs text-muted-foreground">
            {pedido.meu ? 'Você pediu' : `${pedido.solicitante_nome ?? 'Um membro'} pediu`} em{' '}
            {formatDate(pedido.created_at)}
            {pedido.decidido_em && (pedido.status === 'APROVADO' || pedido.status === 'RECUSADO') && (
              <>
                {' · '}
                {pedido.status === 'APROVADO' ? (exclusao ? 'excluído' : 'retirado') : 'recusado'}
                {pedido.decidido_por_nome && ` por ${pedido.decidido_por_nome}`} em {formatDate(pedido.decidido_em)}
              </>
            )}
          </p>
          {pendente && decide && exclusao && (
            <p className="text-xs text-muted-foreground">
              Aprovado, o processo é excluído, com os documentos dele. Recusado, continua no escritório.
            </p>
          )}
          {pendente && decide && !exclusao && (
            <p className="text-xs text-muted-foreground">
              Aprovado, os colegas deixam de ver
              {pedido.recurso_tipo === 'PROCESSO'
                ? ' o processo, com as intimações e os documentos dos autos'
                : ' o item'}
              . Recusado, continuam só lendo.
            </p>
          )}
        </div>
        {pendente && (decide || pedido.meu) && (
          <div className="flex shrink-0 gap-2">
            {decide && (
              <>
                <Button
                  className="h-11 sm:h-9"
                  variant="outline"
                  onClick={() => decidir.mutate('recusar')}
                  disabled={decidir.isPending}
                >
                  {acao === 'recusar' ? (
                    <Spinner size={16} className="mr-2 text-inherit" />
                  ) : (
                    <X className="mr-2 h-4 w-4" />
                  )}
                  Recusar
                </Button>
                <Button className="h-11 sm:h-9" onClick={() => decidir.mutate('aprovar')} disabled={decidir.isPending}>
                  {acao === 'aprovar' ? (
                    <Spinner size={16} className="mr-2 text-inherit" />
                  ) : (
                    <Check className="mr-2 h-4 w-4" />
                  )}
                  Aprovar
                </Button>
              </>
            )}
            {pedido.meu && (
              <Button
                className="h-11 sm:h-9"
                variant="outline"
                onClick={() => decidir.mutate('cancelar')}
                disabled={decidir.isPending}
              >
                {acao === 'cancelar' && <Spinner size={16} className="mr-2 text-inherit" />}
                Cancelar o pedido
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
